import React, { Ref, useRef } from 'react';
import cn from 'classnames';
import {
  components as ReactSelectComponents,
  ValueContainerProps,
  SelectComponentsConfig,
  GroupBase,
} from 'react-select';
import MultiValueContainerContextProvider from '../context/MultiValueContainerContext';
import useValueContainerStyles from '../useSelect/useValueContainerStyles';
import { ISelectOption } from '../interfaces';
import type { SelectSize } from '../interfaces';

import styles from '../select.module.scss';

interface IProps<SOption, IsMulti extends boolean, Group extends GroupBase<SOption>> {
  dataTest: string,
  isLoading?: boolean,
  isSlim?: boolean,
  size?: SelectSize,
  isUseResponsiveTagsCount?: boolean,
  components?: SelectComponentsConfig<SOption, IsMulti, Group>,
  ownProps: ValueContainerProps<SOption, IsMulti, Group>,
}

const MultiValueContainer = <SOption, IsMulti extends boolean, Group extends GroupBase<SOption>>({
  containerRef,
  dataTest,
  isLoading,
  isSlim,
  size,
  isUseResponsiveTagsCount,
  components = {},
  ownProps,
}: IProps<SOption, IsMulti, Group> & {
  containerRef: Ref<HTMLDivElement>,
}) => {
  const dataTestAttr = `${ dataTest }_value_container`;
  const style = useValueContainerStyles({ components, isLoading, isSlim, size, ownProps });
  const { children, ...otherProps } = ownProps;

  const Component = components.ValueContainer || ReactSelectComponents.ValueContainer;

  return (
    <div
      ref={ containerRef }
      data-test={ dataTestAttr }
      className={ cn(styles.multiValueContainer, {
        [styles.multiValueContainerNoWrap]: isUseResponsiveTagsCount,
      }) }
      style={ style as React.CSSProperties }
    >
      <Component { ...otherProps }>
        { children }
      </Component>
    </div>
  );
};

export default <SOption, IsMulti extends boolean, Group extends GroupBase<SOption>>(
  props: IProps<SOption, IsMulti, Group>,
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const value = props.ownProps.selectProps.value as ISelectOption[];

  return (
    <MultiValueContainerContextProvider
      value={ value }
      containerRef={ containerRef }
    >
      <MultiValueContainer { ...props } containerRef={ containerRef } />
    </MultiValueContainerContextProvider>
  );
};
